<template>
    <div class="plan-history d-flex flex-column flex-fill" style="background: #F5F6F8; overflow: scroll;" :style="{padding: isAttendance ? '16px 16px 0 16px' : '16px'}">
        <fe-dialog
            title="Participation Details"
            v-if="fidelityDialog.show"
            dismissButtonText="Cancel"
            acceptButtonText="Done"
            width="800"
            @accept="loadStudent"
            @close="fidelityDialog.show=false"
            persistent
            height="650"
        >
            <fidelity :params="fidelityDialog.params"/>
        </fe-dialog>

        <v-expansion-panels multiple v-model="openPanels" :disabled="isAttendance" :flat="isAttendance">
            <v-expansion-panel
                v-for="(p,i) in plans"
                class="plan-container"
                :style="{background: p.active_phase ? 'white' : '#ECEDF1'}"
                @change="checkPlan(p, i)"
            >
                <v-expansion-panel-header :style="{display: isAttendance ? 'block' : 'flex', borderBottom: isAttendance ? 'none' : '1px solid #dddddd'}">
                    <div v-if="isAttendance" style="font-size: 18px; color: #050F2D;">
                        Plan History
                    </div>
                    <div class="d-flex">
                        <div class="plan-title">
                            {{ p.intervention_plan_name }}

                            <div class="plan-detail d-flex">
                                <div style="padding-top: 2px;" class="mr-2">
                                    {{ p.intervention_type_name.substring(0, 20) }}
                                </div>

                                <v-icon style="font-size: 6px">fas fa-circle</v-icon>

                                <div style="padding-top: 2px;" class="mx-2">{{ interventionists(p) }}</div>

                                <v-icon v-if="!isAttendance" style="font-size: 6px">fas fa-circle</v-icon>

                                <div v-if="!isAttendance" style="padding-top: 2px;" class="mx-2">
                                    {{ p.active_phase ? 'Active Phase' : 'Past Phase' }}
                                </div>
                            </div>
                        </div>

                        <v-spacer/>
                        <div style="font-size: 14px; padding-top: 10px; margin-right: 8px; color: #050F2D">
                            {{ $dayjs(p.student_intervention_plan_effective_start_date).format('MMM DD') }} -
                            {{ $dayjs(p.student_intervention_plan_effective_end_date).format('MMM DD, YYYY') }}
                        </div>
                    </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content style="min-width: 800px;">
                    <v-row v-if="!isAttendance" style="min-height: 100px">
                        <v-col
                            :cols="flexCols"
                            class="fidelity-container"
                            :class="{'bottom-border': smallScreen}"
                            role="button"
                            tabindex="0"
                            @keydown.enter="showFidelity(p)"
                            @keydown.space.prevent="showFidelity(p)"
                            aria-label="Show Fidelity"
                            data-test="plan-history-fidelity-btn"
                            @click="showFidelity(p)"
                        >
                            <div class="d-flex">
                                <div class="title-bold" style="color: #2B3963; font-size: 14px;">PROCEDURES</div>
                                <v-spacer/>
                            </div>

                            <div class="d-flex" style="align-items: baseline;">
                                <div class="mr-2" style="font-size: 24px;">
                                    {{ (p && p.procedures && p.procedures.length) || 'NA' }}
                                </div>
                                <div>Procedures</div>
                            </div>

                            <div class="d-flex mt-2" style="flex-direction: column;">
                                <div v-for="proc in p.procedures" :key="proc.id" class="d-flex" style="justify-content: space-between; width: 100%;">
                                    <fe-tooltip style="width: 80%;" :tooltip="proc.name">
                                        <div class="procedure-truncate">{{ proc.name }}</div>
                                    </fe-tooltip>
                                    <div>{{ proc.pct }}%</div>
                                </div>
                            </div>
                        </v-col>
                        <v-col
                            :cols="flexCols"
                            :class="{'bottom-border': smallScreen}"
                            role="button"
                            tabindex="0"
                            @keydown.enter="showFidelity(p)"
                            @keydown.space.prevent="showFidelity(p)"
                            aria-label="Show Fidelity"
                            data-test="plan-history-fidelity-attendance-btn"
                            @click="showFidelity(p)"
                        >
                            <div class="d-flex">
                                <div class="title-bold" style="color: #2B3963; font-size: 14px;">ATTENDANCE</div>
                                <v-spacer/>
                            </div>

                            <div class="d-flex" style="align-items: baseline;">
                                <div class="mr-2" style="font-size: 24px;">{{ p.unrecorded_days }}</div>
                                <div>Unrecorded Days</div>
                            </div>

                            <div class="mt-2">
                                <v-icon v-if="p.fidelity >= 80" color="green">fas fa-check</v-icon>
                                <v-icon v-else-if="p.fidelity > 70 && p.fidelity < 80" color="orange">fas fa-exclamation</v-icon>
                                <v-icon v-else color="red">fas fa-times</v-icon>
                                <span class="ml-2" :style="{color: getFidelityColor(p.fidelity)}">{{ p.fidelity }}% Participation</span> 
                            </div>
                        </v-col>
                    </v-row>
                    <v-divider/>
                    <v-row v-if="!isAttendance">
                        <v-col
                            :cols="flexCols"
                            class="monitor-container"
                            :class="{'bottom-border': smallScreen}"
                            @click="$emit('monitor-click')"
                        >
                            <div class="d-flex">
                                <div class="title-bold" style="color: #2B3963; font-size: 14px;">PROGRESS MONITORING</div>
                                <v-spacer/>
                                <template v-if="p.category_id == 4">
                                    <div
                                        v-if="p.primary_probe && !p.primary_probe.goal && p.primary_probe.four_week_possible_pct < 80"
                                        style="color: red; font-size: 12px !important;"
                                    >
                                        <v-icon color="red" class="mr-2">fas fa-exclamation</v-icon>
                                        Not On Track
                                    </div>

                                    <div
                                        v-else-if="p.primary_probe && p.primary_probe.goal > p.primary_probe.four_week_possible_pct"
                                        style="color: red; font-size: 12px !important;"
                                    >
                                        <v-icon color="red" class="mr-2">fas fa-exclamation</v-icon>
                                        Not On Track
                                    </div>

                                    <div v-else style="color: green; font-size: 12px !important;">
                                        <v-icon color="success" class="mr-2">fas fa-check</v-icon>
                                        On Track
                                    </div>
                                </template>
                                <template v-else>
                                    <div v-if="p.primary_probe && p.primary_probe.on_track"
                                         style="color: green; font-size: 12px !important;"
                                    >
                                        <v-icon color="success" class="mr-2">fas fa-check</v-icon>
                                        On Track
                                    </div>

                                    <div v-else-if="p.primary_probe && p.primary_probe.not_on_track"
                                         style="color: red; font-size: 12px !important;"
                                    >
                                        <v-icon color="warning" class="mr-2">fas fa-times</v-icon>
                                        Not On Track
                                    </div>

                                    <div
                                        v-else-if="p.primary_probe && (p.primary_probe.no_roi || p.primary_probe.no_goal)"
                                        style="color: gray; font-size: 12px !important;"
                                    >
                                        <v-icon color="gray" class="mr-2">fas fa-question-circle</v-icon>
                                        No Data
                                    </div>
                                </template>
                            </div>
                            
                            <div class="d-flex">
                                <div class="fidelity-stats" style="width: 25%;">
                                    <div class="stats-count">
                                        {{ (p && p.primary_probe && p.primary_probe.roi) || 'NA' }}
                                    </div>

                                    <div class="stats-detail">ROI</div>
                                </div>

                                <div class="fidelity-stats" style="width: 25%;">
                                    <div class="stats-count" style="width: 25%;">
                                        {{ (p && p.primary_probe && p.primary_probe.latest_score) || 'NA' }}
                                    </div>

                                    <div class="stats-detail">Latest Score</div>
                                </div>

                                <div class="fidelity-stats" style="width: 25%;">
                                    <div class="stats-count" style="width: 25%;">
                                        {{ (p && p.primary_probe && p.primary_probe.anticipated_final_score) || 'NA' }}
                                    </div>

                                    <div class="stats-detail">Projected Score</div>
                                </div>

                                <div class="fidelity-stats" style="width: 25%;">
                                    <div class="stats-count" style="width: 25%;">
                                        {{ (p && p.achieve_goal_date) ? $dayjs(p.achieve_goal_date).format('M/D/YYYY') : 'NA' }}
                                    </div>

                                    <div class="stats-detail">Estimated Goal Date</div>
                                </div>
                            </div>
                        </v-col>
                        <v-col
                            :cols="flexCols"
                            class="decision-container"
                            :class="{'bottom-border': smallScreen}"
                            @click="$emit('message-click')"
                        >
                            <div class="d-flex">
                                <div class="title-bold" style="color: #2B3963; font-size: 14px;">DECISION LOG</div>
                                <v-spacer/>
                                <v-icon>fas fa-comments</v-icon>
                            </div>

                            <div style="overflow: scroll; max-height: 150px;" class="mt-2">
                                <div style="font-size: 12px; color: #7E8494;" v-for="m in p.decisions" class="d-flex">
                                    <div v-safe-html="m.created" style="width: 200px;"></div>

                                    <div v-safe-html="stripCommon(m.value)" class="decision-message"></div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="isAttendance" style="min-height: auto">
                        <v-col
                            :cols="flexCols"
                            role="button"
                            tabindex="0"
                            @keydown.enter="showFidelity(p)"
                            @keydown.space.prevent="showFidelity(p)"
                            aria-label="Show Fidelity"
                            data-test="plan-history-fidelity-is-attendance-btn"
                            @click="showFidelity(p)"
                        >
                            <div class="d-flex fidelity-container-attendance">
                                <div class="d-flex" style="width: 50%; justify-content: space-between;">
                                    <div style="color: #2B3963; font-size: 14px;">FIDELITY</div>
                                    <template class="d-flex">
                                        <div class="fidelity-stats-attendance">
                                            <div class="stats-count-attendance">
                                                {{ (p && p.procedures && p.procedures.length) || 'NA' }} Procedures</div>
                                        </div>
                                        <div class="fidelity-stats-attendance">
                                            <div class="stats-count-attendance">{{ p.unrecorded_days }} Unrecorded Days</div>
                                        </div>
                                    </template>
                                    </div>
                                <div class="d-flex" style="justify-content: flex-end;width: 50%;">
                                    <v-icon v-if="p.fidelity >= 80" color="green">fas fa-check</v-icon>
                                    <v-icon v-else-if="p.fidelity > 70 && p.fidelity < 80" color="orange">fas fa-exclamation</v-icon>
                                    <v-icon v-else color="red">fas fa-times</v-icon>
                                    <span class="ml-2" :style="{color: getFidelityColor(p.fidelity)}">{{ p.fidelity }}% Participation</span> 
                                </div>
                            </div>

                            <div class="d-flex mt-2 ml-3" style="flex-direction: column;">
                                <div v-for="proc in p.procedures" :key="proc.id" class="d-flex" style="justify-content: space-between; width: 50%; color: #050F2D;">
                                    <fe-tooltip style="width: 80%;" :tooltip="proc.name">
                                        <div class="procedure-truncate">{{ proc.name }}</div>
                                    </fe-tooltip>
                                    <div>{{ proc.pct }}%</div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import Fidelity from './Fidelity'

export default {
    name: 'PlanHistory',

    components: {Fidelity},

    props: {
        params: {
            type: Object,
            default: () => {
            }
        },
        reload: {
            type: Boolean,
            default: false
        },
        currentPlan: {
            type: Object,
            default: () => {
            }
        },
        isAttendance: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            plans: [],
            openPanels: [],
            fidelityDialog: {
                show: false
            },
            windowWidth: window.innerWidth
        }
    },

    computed: {
        flexCols() {
            if (this.isAttendance) {
                return 12
            }

            if (this.smallScreen) {
                return 12
            }

            return 6
        },
        smallScreen() {
            return this.windowWidth < 1100
        }
    },

    watch: {
        reload(v) {
            if (v) {
                this.loadStudent()
            }
        },
    },

    mounted() {
        window.addEventListener('resize', () => this.setBreaksize())
        this.loadStudent()
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setBreaksize())
    },

    methods: {
        interventionists(plan) {
            return plan.interventionists.map(interventionist => interventionist.name).join(', ')
        },

        setBreaksize() {
            this.windowWidth = window.innerWidth
        },

        showFidelity(rec) {
            this.fidelityDialog.show = true
            this.fidelityDialog.params = {
                student_id: rec.student_id,
                intervention_id: rec.intervention_id,
                intervention_plan_id: rec.intervention_plan_id
            }
        },

        checkPlan(plan, i) {
            if (plan.planLoaded) {
                return;
            }
            this.$modelGet('interventionPlanStudent', {
                student_id: plan.student_id,
                intervention_plan_id: plan.intervention_plan_id
            })
            .then(response => {
                let r = response[0]
                r.active_phase = (i === 0 ? true : false)
                r.planLoaded = true

                this.$emit('setInterventionId', r.intervention_id)

                this.$modelGet('interventionPlanComments', {
                    student_id: plan.student_id,
                    intervention_plan_id: plan.intervention_plan_id
                })
                .then(messages => {
                    r.decisions = messages
                        .filter(r => r.message_type_id == 3)
                        .map(r => {
                            r.created = this.$dayjs(r.created).format('MMM D, YYYY')
                            return r
                        })
                    // trigger view refresh
                    let newPlans = [...this.plans]
                    newPlans[i] = r
                    this.plans = newPlans
                    // end trigger
                })
            })
            .finally(() => {

            })
        },

        stripCommon(v) {
            return v.replace(/At the meeting(.+?),/, '')
        },

        getFidelityColor(v) {
            if (v < 70) return 'red'
            if (v < 80) return 'orange'
            return 'green'
        },

        loadStudent() {
            this.plans = []
            this.$modelGet('interventionPlanStudent', this.$props.params)
                .then(records => {
                    records.forEach(p => {
                        p.previous_plans.reverse().forEach((prev, i) => {
                            prev.active_phase = i === 0 ? true : false
                            prev.past_phase = i > 0
                            prev.planLoaded = false
                            this.plans.push(prev)
                        })
                    })
                    // set panel open that's related to the actual intervention;
                    // used to always set first intervention to open (so % not always matching)
                    if (!this.openPanels.length) {
                        this.openPanels = [this.plans.findIndex(x => x.intervention_plan_id == this.currentPlan.id)]
                    }
                    this.openPanels.forEach(i => this.checkPlan(this.plans[i], i))
                })
                .finally(() => {
                    this.$emit('loaded-student')
                })
        }
    }
}
</script>

<style lang="scss" scoped>
.plan-history {
    ::v-deep .v-expansion-panel-content__wrap {
        padding: 0 !important;
    }
}

.bottom-border {
    border: 1px solid #E0E1E6;
}

.plan-container {
    background: #FFFFFF;
    border: 1px solid #E0E1E6;
    box-sizing: border-box;
    border-radius: 4px 4px 0 0;
    margin: 8px;

    .plan-title {
        font-size: 14px;
        font-weight: 500;
        color: #2B3963;

        .plan-detail {
            font-size: 12px;
            line-height: 16px;
        }
    }

    .fidelity-container {
        cursor: pointer;
        border-right: 1px solid #dddddd;
        padding: 16px;

        &-attendance {
            align-items: center;
            padding-left: 12px;
            padding-right: 12px;
        }

        .fidelity-stats {
            width: 50%;
        }
    }

    .fidelity-container:has(.fidelity-container-attendance) {
        border-right: none !important;
    }

    .monitor-container {
        cursor: pointer;
        border-right: 1px solid #dddddd;
        flex: 1 1 auto;
        padding: 16px;
    }

    .decision-container {
        cursor: pointer;
        padding: 16px;
    }
}

.stats-count {
    font-size: 24px;
    line-height: 48px;
    height: 48px;
    color: #050F2D;
    &-attendance {
        color: #050F2D;
        font-size: 14px;
    }
}

.stats-detail {
    font-size: 12px;
}

.procedure-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.title-bold {
    font-family: 'CerebriSans-Medium', Cerebri Sans, 'Roboto', sans-serif !important;
}
</style>
